import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import {
  Form,
  Row,
  Col,
  OverlayTrigger,
  Tooltip,
  Dropdown,
} from "react-bootstrap";
import "bootstrap-icons/font/bootstrap-icons.css";
import "./CarrierForm.css";
import useApi from "../../apiProvider";
import toast from "react-hot-toast";
import { useParams, useNavigate } from "react-router-dom";
import CancelModal from "./CancelModal";
import ContactsTable from "./ContactsTable";
import CarrierUploadModal from "./CarrierUploadModal";
import CarrierUploadSelectModal from "./CarrierUploadSelectModal";
import ContactUploadSelectModal from "./ContactUploadSelectModal";
import Icon from "../generic/Icon";
import DeleteModal from "./DeleteModal";
import Papa from "papaparse";
// import Papa from "papaparse";
import { useRoleContext } from "../ContextFinalBuild/Providers/RoleProvider";
import { saveAs } from "file-saver";

const CarrierForm = () => {
  // Stable ref for handleselectedCarrier
  const handleselectedCarrierRef = useRef();

  const [loading, setLoading] = useState(false);
  const [showCarrierSelectModal, setShowCarrierSelectModal] = useState(false);
  const [showContactSelectModal, setShowContactSelectModal] = useState(false);
  const [selectedCarrierIds, setSelectedCarrierIds] = useState([]);
  const [selectedContactIds, setSelectedContactIds] = useState([]);
  const [carrierData, setcarrierData] = useState([]);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadMessage, setUploadMessage] = useState("");
  const [languageData, setLanguageData] = useState([]);
  const [timezoneData, setTimezoneData] = useState([]);
  const [contactData, setContactData] = useState([]);
  const [filteredNames, setFilteredNames] = useState([]);
  const [onboardDateInput, setOnboardDateInput] = useState("");
  const [offboardDateInput, setOffboardDateInput] = useState("");
  const [newNames, setNewNames] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [isFormModified, setIsFormModified] = useState(false); // Track if form is modified
  const [isContactsModified, setIsContactsModified] = useState(false); // Track if contacts are modified
  const [isNameSelected, setIsNameSelected] = useState(false); // Track if Name is selected
  const [selectedCarrier, setselectedCarrier] = useState({}); // Track selected carrier details
  const [previousCarrier, setPreviousCarrier] = useState({});
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelData, setCancelData] = useState({});
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteData, setDeleteData] = useState({});
  const [modalType, setModalType] = useState("");
  const [originalContact, setOriginalContact] = useState(null);
  const [contacts, setContacts] = useState([]);
  const { paramCarrierName } = useParams();
  const [editId, setEditId] = useState(null); // Track the ID of the contact being edited
  const [selectedOptions, setSelectedOptions] = useState({
    Role: [],
    Type: [],
    Region: [],
    Timezone: [],
    Language: [],
  });
  const [isSortingLocked, setIsSortingLocked] = useState(false);
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: null,
  });

  const navigate = useNavigate();
  const divRef = useRef(null);
  const { isAdmin } = useRoleContext();

  // Handle full filter data for contacts
  const allFilterDataForContacts = useMemo(() => {
    const currentContacts = contacts || [];
    if (!currentContacts.length) {
      return { Role: [], Type: [], Region: [], Timezone: [], Language: [] };
    }
    return {
      Role: [
        ...new Set(
          currentContacts.map((contact) => contact.role).filter(Boolean),
        ),
      ].map((name) => ({ name })),
      Type: [
        ...new Set(
          currentContacts.map((contact) => contact.type).filter(Boolean),
        ),
      ].map((name) => ({ name })),
      Region: [
        ...new Set(
          currentContacts.map((contact) => contact.region).filter(Boolean),
        ),
      ].map((name) => ({ name })),
      Timezone: [
        ...new Set(
          currentContacts
            .map((contact) => contact.timezoneCode)
            .filter(Boolean),
        ),
      ].map((name) => ({ name })),
      Language: [
        ...new Set(
          currentContacts.map((contact) => contact.language).filter(Boolean),
        ),
      ].map((name) => ({ name })),
    };
  }, [contacts]);

  // Define the custom role order
  const defaultSort = ["Support", "Escalation", "Account", "Billing", "Admin"];

  const sortedAndFilteredContacts = contacts
    .filter((contact) => {
      // Filtering logic
      // Always include new contacts being added, regardless of filters
      if (contact.id === -1) return true;

      const matchesRole =
        selectedOptions["Role"].length === 0 ||
        selectedOptions["Role"].includes(contact.role);

      const matchesType =
        selectedOptions["Type"].length === 0 ||
        selectedOptions["Type"].includes(contact.type);

      const matchesRegion =
        selectedOptions["Region"].length === 0 ||
        selectedOptions["Region"].includes(contact.region);

      const matchesTimezone =
        selectedOptions["Timezone"].length === 0 ||
        selectedOptions["Timezone"].includes(contact.timezoneCode);

      const matchesLanguage =
        selectedOptions["Language"].length === 0 ||
        selectedOptions["Language"].includes(contact.language);

      return (
        matchesRole &&
        matchesType &&
        matchesRegion &&
        matchesTimezone &&
        matchesLanguage
      );
    })
    .map((contact, index) => ({
      ...contact,
      _originalIndex: index, // Add original position index
    }))
    .sort((a, b) => {
      // Get the effective role for sorting - use originalContact role if the row is being edited
      const getEffectiveRole = (contact) => {
        if (contact.id === editId && originalContact) {
          return originalContact.role; // Use original role for the row being edited
        }
        return contact.role; // Use current role for other rows
      };

      // Skip custom sorting if sorting is locked but still allow default sorting
      if (isSortingLocked) {
        // Apply default sorting even when locked
        const aRole = getEffectiveRole(a);
        const bRole = getEffectiveRole(b);

        const aIndex = defaultSort.indexOf(aRole);
        const bIndex = defaultSort.indexOf(bRole);

        // Handle roles not in the custom order
        const aPriority = aIndex === -1 ? Infinity : aIndex;
        const bPriority = bIndex === -1 ? Infinity : bIndex;

        if (aPriority !== bPriority) {
          return aPriority - bPriority;
        }

        // Secondary sort: Alphabetical by name
        const aName = a.name?.toLowerCase() || "";
        const bName = b.name?.toLowerCase() || "";

        return aName.localeCompare(bName);
      }

      // Exclude unsaved contacts from sorting
      if (!a.id && !b.id) return 0; // Keep unsaved contacts in their current order
      if (!a.id) return 1; // Place unsaved contacts at the bottom
      if (!b.id) return -1; // Place unsaved contacts at the bottom

      // Default sort logic: Primary sort by role, Secondary sort by name
      if (!sortConfig.key) {
        const aIndex = defaultSort.indexOf(a.role);
        const bIndex = defaultSort.indexOf(b.role);

        // Handle roles not in the custom order
        const aPriority = aIndex === -1 ? Infinity : aIndex;
        const bPriority = bIndex === -1 ? Infinity : bIndex;

        if (aPriority !== bPriority) {
          return aPriority - bPriority;
        }

        // Secondary sort: Alphabetical by name
        const aName = a.name?.toLowerCase() || "";
        const bName = b.name?.toLowerCase() || "";

        return aName.localeCompare(bName);
      }

      // Primary sort: Selected column
      const aValue = a[sortConfig.key]?.toString().toLowerCase() || "";
      const bValue = b[sortConfig.key]?.toString().toLowerCase() || "";

      if (aValue < bValue) {
        return sortConfig.direction === "asc" ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === "asc" ? 1 : -1;
      }

      // Secondary sort: Alphabetical by name
      const aName = a.name?.toLowerCase() || "";
      const bName = b.name?.toLowerCase() || "";

      return aName.localeCompare(bName);
    });

  const handleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        // Cycle through ascending, descending, and default (no sorting)
        if (prev.direction === "asc") {
          return { key, direction: "desc" }; // Switch to descending
        } else if (prev.direction === "desc") {
          return { key: null, direction: null }; // Reset to default
        }
      }
      return { key, direction: "asc" }; // Default to ascending
    });
  };

  const getInputStyle = (baseStyle) => ({
    ...baseStyle,
    cursor: "default",
    opacity: !isAdmin ? 0.7 : 1,
  });

  const handleselectedCarrier = useCallback(
    async (carrier) => {
      setIsFormModified(false);
      setselectedCarrier(carrier); // set the selected carrier details
      // if carrier is the same name as before leave contacts as is
      if (
        previousCarrier &&
        previousCarrier.carrierName !== carrier.carrierName
      ) {
        let contacts = await useApi.getContactsByCarrierName(
          carrier.carrierName.replace(/\//g, "_"),
        );
        setContacts([]); // Reset contacts when a new carrier is selected

        // Check if contacts exist and update the state
        if (contacts) {
          setContacts(
            contacts.map((contact) => ({
              ...contact,
            })),
          );
        }

        setEditId(null); // Reset the edit index
        setIsContactsModified(false);
      }

      // Set the contacts for the selected carrier
      setInputValue(carrier.carrierName);
      setPreviousCarrier(carrier);
      setFilteredNames([]);
      setIsNameSelected(true); // Set Name as selected
    },
    [previousCarrier],
  );

  useEffect(() => {
    handleselectedCarrierRef.current = handleselectedCarrier;
  }, [handleselectedCarrier]);

  useEffect(() => {
    const getData = async () => {
      setLoading(true);
      let results = await useApi.getAllCarriers();
      console.log("Fetched carriers:", results);
      let languageData = await useApi.getAllLanguages();
      let timezoneData = await useApi.getAllTimezones();
      let contactResults = await useApi.getAllContacts();
      setcarrierData(results);
      setContactData(contactResults.returnData || []);
      console.log("Fetched contacts:", contactResults.returnData || []);
      if (paramCarrierName)
        handleselectedCarrierRef.current(
          results.find(
            (c) => c.carrierName === paramCarrierName.replace("_", "/"),
          ),
        );
      setLanguageData(languageData);
      setTimezoneData(timezoneData);
      setLoading(false);
    };
    getData();
  }, [paramCarrierName]);

  useEffect(() => {
    if (selectedCarrier.onboardDate) {
      setOnboardDateInput(formatDate(selectedCarrier.onboardDate));
    } else {
      setOnboardDateInput("");
    }
    if (selectedCarrier.offboardDate) {
      setOffboardDateInput(formatDate(selectedCarrier.offboardDate));
    } else {
      setOffboardDateInput("");
    }
  }, [selectedCarrier]);

  const handleOnboardDateChange = (e) => {
    setOnboardDateInput(e.target.value);
    setIsFormModified(true);
  };

  const isValidDate = (dateString) => {
    // Check format yyyy-MM-dd
    const regex = /^\d{4}-\d{2}-\d{2}$/;
    if (!regex.test(dateString)) return false;
    const date = new Date(dateString);
    return !isNaN(date.getTime());
  };

  const handleOnboardDateBlur = () => {
    if (!onboardDateInput || !isValidDate(onboardDateInput)) {
      toast.error("Please enter a valid date ");
      setOnboardDateInput(""); // Optionally clear invalid input
      return;
    }
    setselectedCarrier((prev) => ({
      ...prev,
      onboardDate: convertToEpoch(onboardDateInput),
    }));
  };

  const handleOffboardDateChange = (e) => {
    setOffboardDateInput(e.target.value);
    setIsFormModified(true);
  };

  const handleOffboardDateBlur = () => {
    if (!offboardDateInput || !isValidDate(offboardDateInput)) {
      toast.error("Please enter a valid date");
      setOffboardDateInput(""); // Optionally clear invalid input
      return;
    }
    setselectedCarrier((prev) => ({
      ...prev,
      offboardDate: convertToEpoch(offboardDateInput),
    }));
  };

  const handleCSVUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    Papa.parse(file, {
      header: true,
      complete: async (results) => {
        const csvData = results.data;
        const headers = results.meta.fields || [];
        setLoading(true);
        try {
          if (
            headers.includes("carrierName") &&
            headers.includes("status") &&
            headers.includes("onboardDate")
          ) {
            const response = await useApi.uploadCarrier(csvData);
            setcarrierData(response.returnData || []);
            // Reset all relevant state to initial values
            setselectedCarrier({});
            setContacts([]);
            setInputValue("");
            setFilteredNames([]);
            setNewNames([]);
            setIsFormModified(false);
            setIsNameSelected(false);
            setPreviousCarrier({});
            setOnboardDateInput("");
            setOffboardDateInput("");
            setUploadMessage(response.returnMessage); // Set the message
            setShowUploadModal(true); // Show the modal if not already
          } else if (
            headers.includes("carrierName") &&
            headers.includes("name") &&
            headers.includes("email")
          ) {
            const response = await useApi.uploadContact(csvData);
            setcarrierData(response.returnData || []);
            // Reset all relevant state to initial values
            setselectedCarrier({});
            setContacts([]);
            setInputValue("");
            setFilteredNames([]);
            setNewNames([]);
            setIsFormModified(false);
            setIsNameSelected(false);
            setPreviousCarrier({});
            setOnboardDateInput("");
            setOffboardDateInput("");
            setUploadMessage(response.returnMessage); // Set the message
            setShowUploadModal(true); // Show the modal if not already
          } else {
            toast.error("CSV headers do not match expected formats.");
          }
        } catch (error) {
          console.error(error);
        } finally {
          setLoading(false);
        }
        event.target.value = "";
      },
      error: (err) => {
        toast.error("Failed to parse CSV file");
        console.error(err);
      },
    });
  };

  function formatForecastDate(timestamp) {
    const userLocale = navigator.language;
    if (timestamp) {
      const date = new Date(timestamp * 1000);
      const day = String(date.getDate()).padStart(2, "0");
      const month = date.toLocaleString(userLocale, { month: "short" }); // e.g., 'May'
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    }
    return null;
  }

  const downloadPopulatedCarrierTemplate = () => {
    setShowCarrierSelectModal(true);
  };
  const downloadPopulatedContactTemplate = () => {
    setShowContactSelectModal(true);
  };
  // Helper to fetch and process the template
  const handleCarrierTemplateDownload = async () => {
    // Prepare your carrier data as an array of objects
    const selectedCarriers = carrierData.filter((carrier) =>
      selectedCarrierIds.includes(carrier.id),
    );

    // Convert carrier data to worksheet rows (adjust keys to match your template columns)
    const newRows = selectedCarriers.map((carrier) => {
      const [region1, region2, region3, region4] = Array.isArray(
        carrier.regions,
      )
        ? carrier.regions
        : [];
      return {
        carrierName: carrier.carrierName,
        status: carrier.status,
        onboardDate: formatForecastDate(carrier.onboardDate),
        offboardDate: formatForecastDate(carrier.offboardDate),
        address1: carrier.address1,
        address2: carrier.address2,
        city: carrier.city,
        state: carrier.state,
        country: carrier.country,
        postcode: carrier.postcode,
        notes: carrier.notes,
        regions: region1 || "",
        regions2: region2 || "",
        regions3: region3 || "",
        regions4: region4 || "",
        // ...add more fields as needed
      };
    });

    // Convert to CSV
    const csv = Papa.unparse(newRows);

    // Download as CSV
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    saveAs(blob, "cvp_carrier_template_filled.csv");
  };

  const handleContactTemplateDownload = async () => {
    // Prepare your contact data as an array of objects
    const selectedContacts = contactData.filter((contact) =>
      selectedContactIds.includes(contact.id),
    );

    // Convert contact data to worksheet rows (adjust keys to match your template columns)
    const newRows = selectedContacts.map((contact) => {
      // Find the carrier name using carrierId
      const carrierName =
        carrierData.find((c) => c.id === contact.carrierId)?.carrierName || "";

      return {
        carrierName, // Use the looked-up carrier name
        name: contact.name,
        role: contact.role,
        type: contact.type,
        email: contact.email,
        mainPhone: contact.mainPhone,
        altPhone: contact.altPhone,
        timezoneCode: contact.timezoneCode,
        language: contact.language,
        notes: contact.notes,
        region: contact.region,
        // ...add more fields as needed
      };
    });

    // Convert to CSV
    const csv = Papa.unparse(newRows);

    // Download as CSV
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    saveAs(blob, "cvp_contact_template_filled.csv");
  };

  const downloadCarrierContactBlankTemplate = () => {
    const link = document.createElement("a");
    link.href = "CVP_Carrier_Contact_Template.xlsx";
    link.download = "CVP_Carrier_Contact_Template.xlsx";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const downloadCarrierBlankTemplate = () => {
    const link = document.createElement("a");
    link.href = "CVP_Carrier_Template.xlsx";
    link.download = "CVP_Carrier_Template.xlsx";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const cancelConfirm = () => {
    if (selectedCarrier.id === null || selectedCarrier.id === undefined) {
      setselectedCarrier({});
      setInputValue("");
      setIsFormModified(false);
      setIsNameSelected(false);
      return;
    }
    setselectedCarrier(carrierData.find((s) => s.id === selectedCarrier.id));
    setIsFormModified(false);
  };

  const handleNameChange = (e) => {
    const value = e.target.value;

    // Reset URL to base path when user starts searching
    if (paramCarrierName) {
      navigate("/carrier");
    }

    setInputValue(value);
    console.log("Name:", value);
    console.log("carrier data:", carrierData);

    const filtered = carrierData.filter((carrier) =>
      carrier.carrierName.toLowerCase().includes(value.toLowerCase().trim()),
    );
    const exactMatchNames = carrierData.filter(
      (carrier) =>
        carrier.carrierName.toLowerCase() === value.toLowerCase().trim(),
    );
    setNewNames(exactMatchNames);
    setFilteredNames(filtered);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    const newValue =
      name === "onboardDate" || name === "offboardDate"
        ? convertToEpoch(value)
        : value;
    setselectedCarrier((prev) => ({ ...prev, [name]: newValue }));
    setIsFormModified(true); // Set form as modified
  };

  const handleEdit = (id) => {
    setEditId(id); // Set the ID of the contact being edited
    const contactToEdit = contacts.find((contact) => contact.id === id);
    if (!contactToEdit) {
      console.error("Contact not found. Cannot edit.");
      return;
    }
    setOriginalContact({ ...contactToEdit }); // Save the original contact data
    setIsContactsModified(false);
    setIsSortingLocked(true); // Lock sorting while editing
  };

  const handleCancelEdit = () => {
    // Check if new contact is being added
    const isNewContact = editId === -1;

    if (isNewContact) {
      // Remove the temporary contact from the array
      setContacts((prevContacts) =>
        prevContacts.filter((contact) => contact.id !== -1),
      );
    } else if (originalContact) {
      // For existing contacts, restore the original values
      setContacts((prevContacts) =>
        prevContacts.map((contact) =>
          contact.id === editId ? originalContact : contact,
        ),
      );
    }

    // Reset edit state
    setEditId(null);
    setIsContactsModified(false);
    setIsSortingLocked(false);
  };

  const handleSave = async () => {
    const { id } = selectedCarrier;
    const apiMethod = id ? useApi.updateCarrier : useApi.createCarrier;
    const results = await apiMethod(selectedCarrier);

    setcarrierData(results?.returnData);
    setIsFormModified(false);
    // Find the newly saved carrier in the updated carrierData
    const newCarrier = results?.returnData.find(
      (carrier) => carrier.carrierName === selectedCarrier.carrierName,
    );
    handleselectedCarrier(newCarrier);
  };

  const handleSaveContact = async () => {
    const contactToSave = contacts.find((contact) => contact.id === editId);
    if (!contactToSave) {
      console.error("Contact not found. Cannot save.");
      return;
    }

    if (!contactToSave.name) {
      toast.error("Name field is empty. Please enter a name before saving");
      return;
    }

    if (!contactToSave.role) {
      toast.error("Role not selected. Please select a role before saving");
      return;
    }

    // Regular expression for basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (contactToSave.email !== "" && !emailRegex.test(contactToSave.email)) {
      toast.error("Invalid email address. Please enter a valid email address");
      return;
    }

    // Remove ID field from contact before saving
    if (contactToSave.id === -1) {
      contactToSave.id = "";
    }

    // Save/Update contact data
    const apiMethod = contactToSave.id
      ? useApi.updateContact
      : useApi.createContact;
    const result = await apiMethod(contactToSave, selectedCarrier.id);
    // Update the contacts state with the returned data
    setContacts(
      result?.returnData.map((contact, index) => ({
        ...contact,
      })),
    );

    // Reset editId and other states
    setEditId(null);
    setIsContactsModified(false);
    setIsSortingLocked(false);
  };

  const handleDeleteContact = async () => {
    const contactToDelete = contacts.find((contact) => contact.id === editId);
    if (!contactToDelete) {
      console.error("Contact not found. Cannot delete.");
      return;
    }

    if (!contactToDelete.id) {
      console.error("Contact does not have a valid ID. Cannot delete.");
      return;
    }

    // Delete contact data
    const result = await useApi.deleteContact(
      contactToDelete,
      selectedCarrier.id,
    );
    // Update the contacts state with the returned data
    setContacts(
      result?.returnData.map((contact, index) => ({
        ...contact,
      })),
    );

    // Reset editIdx and other states
    setEditId(null);
    setIsContactsModified(false);
    setShowCancelModal(false);
  };

  const handleAddCarrier = (carrierName) => {
    setselectedCarrier((prev) => ({
      ...prev,
      onboardDate: Math.floor(new Date().getTime() / 1000),
    }));
    setselectedCarrier({
      carrierName: carrierName.trim(),
      onboardDate: Math.floor(new Date().getTime() / 1000),
      status: "",
      address1: "",
      address2: "",
      city: "",
      state: "",
      country: "",
      postcode: "",
      regions: [],
      notes: "",
    });

    // Update selectedCarrier state so values can be modified
    setFilteredNames([]);
    setContacts([]);
    setIsNameSelected(true);
    setIsFormModified(true); // Reset Name as not selected
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const updatedContacts = contacts.map((contact) =>
      contact.id === editId ? { ...contact, [name]: value } : contact,
    );
    setContacts(updatedContacts);
    setIsContactsModified(true);
  };

  const [charCount] = useState(1024);

  // Function to convert epoch to yyyy-MM-dd format
  const formatDate = (epoch) => {
    const date = new Date(epoch * 1000); // Multiply by 1000 if epoch is in seconds
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0"); // Months are 0-based
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // Function to convert yyyy-MM-dd to epoch
  const convertToEpoch = (dateString) => {
    const date = new Date(dateString);
    return Math.floor(date.getTime() / 1000); // Convert to seconds
  };

  useEffect(() => {
    const carrierName = localStorage.getItem("carrierName");
    if (carrierName) {
      // setFormData((prev) => ({ ...prev, name: carrierName }));
      localStorage.removeItem("carrierName");
    }
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (divRef.current && !divRef.current.contains(event.target)) {
        // Only clear if no carrier is loaded
        if (
          Object.keys(selectedCarrier).length === 0 ||
          !selectedCarrier.carrierName
        ) {
          setInputValue("");
          setselectedCarrier({});
          setFilteredNames([]);
          setIsFormModified(false);
        } else {
          // If a carrier is loaded, keep it and its name, just close dropdown
          setInputValue(selectedCarrier.carrierName);
          setFilteredNames([]);
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [selectedCarrier]);

  const handleAddContact = () => {
    // Check if there are any unsaved contacts
    const unsavedContact = contacts.find(
      (contact) => contact.id === -1 || !contact.id,
    );
    if (unsavedContact) {
      toast.error(
        "Please save or discard the current new contact before adding a new one.",
      );
      return;
    }

    // Create a new contact with a temporary id of -1
    const newContact = {
      id: -1,
      name: "",
      role: "",
      type: "",
      region: "",
      timezoneCode: "",
      language: "",
      email: "",
      mainPhone: "",
      altPhone: "",
      notes: "",
    };

    // Add the new contact to the contacts array
    const updatedContacts = [...contacts, newContact];
    setContacts(updatedContacts);
    setEditId(-1); // Reset edit ID to allow editing the new contact
    setIsSortingLocked(true);
  };

  const handleRegionChange = (event) => {
    const { value, checked } = event.target;
    setselectedCarrier((prevState) => {
      const regions = prevState.regions || [];
      let newRegions;
      if (checked) {
        // Add the region to the selected regions
        newRegions = [...regions, value];
      } else {
        // Remove the region from the selected regions
        newRegions = regions.filter((region) => region !== value);
      }
      setIsFormModified(true);
      return {
        ...prevState,
        regions: newRegions,
      };
    });
  };

  return (
    <div>
      <div
        className="contentContainer"
        style={{
          padding: "10px",
          backgroundColor: "#ffffff",
          border: "1px solid #ccc",
          borderRadius: "5px",
        }}
      >
        {loading ? (
          <div style={{ textAlign: "center", padding: "40px" }}>
            <span
              className="spinner-border"
              role="status"
              aria-hidden="true"
            ></span>
            <span style={{ marginLeft: "10px" }}>Loading data...</span>
          </div>
        ) : (
          <>
            <Row>
              <Col
                md={12}
                className="d-flex justify-content-between align-items-center mb-3"
              >
                {/* Action Icons - Save/Cancel */}
                {isAdmin && (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "flex-end",
                      marginTop: "25px",
                      alignItems: "center",
                      width: "100%",
                    }}
                  >
                    <Dropdown style={{ marginRight: "20px" }}>
                      <Dropdown.Toggle
                        as="span"
                        style={{
                          cursor: "pointer",
                          fontSize: "1.2rem",
                          color: "#0d6efd",
                        }}
                      >
                        <Icon
                          name="export"
                          overrides={{
                            tooltip: "Download Templates",
                            size: "1.5rem",
                            style: { cursor: "pointer" },
                          }}
                          id="export-csv-icon"
                        />
                      </Dropdown.Toggle>
                      <Dropdown.Menu>
                        <Dropdown.Item onClick={downloadCarrierBlankTemplate}>
                          Download Empty Carrier Template (Excel)
                        </Dropdown.Item>
                        <Dropdown.Item
                          onClick={downloadCarrierContactBlankTemplate}
                        >
                          Download Empty Carrier Contact Template (Excel)
                        </Dropdown.Item>
                        <Dropdown.Item
                          onClick={downloadPopulatedCarrierTemplate}
                        >
                          Download Populated Carrier Template (CSV)
                        </Dropdown.Item>
                        <Dropdown.Item
                          onClick={downloadPopulatedContactTemplate}
                        >
                          Download Populated Contact Template (CSV)
                        </Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown>
                    <input
                      type="file"
                      accept=".csv"
                      onClick={() =>
                        document.getElementById("csv-upload-input").click()
                      }
                      onChange={handleCSVUpload}
                      style={{ display: "none" }}
                      id="csv-upload-input"
                    />
                    <Icon
                      name="upload"
                      onClick={() =>
                        document.getElementById("csv-upload-input").click()
                      }
                      overrides={{
                        tooltip: !isAdmin
                          ? "You need admin privileges to upload"
                          : "Bulk Upload Carriers and Contacts via CSV",
                        disabled: !isAdmin,
                        size: "1.5rem",
                        style: {
                          cursor: "pointer",
                          marginRight: "20px",
                        },
                      }}
                      id="upload-csv-icon"
                    />
                    <Icon
                      name="discard"
                      onClick={
                        isFormModified
                          ? () => {
                              setShowCancelModal(true);
                              setModalType("carrier");
                              setCancelData(selectedCarrier);
                            }
                          : null
                      }
                      overrides={{
                        tooltip: !isAdmin
                          ? "You need admin privileges to edit"
                          : "Discard Carrier Changes",
                        disabled: !isFormModified,
                        size: "1.5rem",
                        style: {
                          marginRight: "20px",
                          cursor: "pointer",
                        },
                      }}
                      id="cancel-carrier-icon"
                    />
                    <Icon
                      name="save"
                      onClick={isFormModified ? handleSave : null}
                      overrides={{
                        tooltip: !isAdmin
                          ? "You need admin privileges to edit"
                          : "Save Carrier Changes",
                        disabled: !isFormModified,
                        size: "1.5rem",
                        style: {
                          cursor: "pointer",
                        },
                      }}
                      id="save-carrier-icon"
                    />
                  </div>
                )}
              </Col>
            </Row>
            <Row>
              <Col md={12}>
                <Form style={{ paddingTop: "10px" }}>
                  <Row className="mb-3">
                    <Col md={4}>
                      <Form.Group
                        controlId="formName"
                        className="d-flex align-items-center mb-1 position-relative"
                      >
                        <Form.Label className="mr-2" style={{ width: "30%" }}>
                          Name
                        </Form.Label>
                        <OverlayTrigger
                          placement="auto"
                          overlay={
                            <Tooltip id="carrier tootltip">
                              Enter the name of a Carrier to search
                            </Tooltip>
                          }
                          offset={[0, 0.5]}
                        >
                          <Form.Control
                            type="text"
                            placeholder="Search..."
                            style={{ width: "70%" }}
                            value={inputValue}
                            onChange={handleNameChange}
                          />
                        </OverlayTrigger>
                        {(filteredNames.length > 0 ||
                          (inputValue &&
                            newNames.length === 0 &&
                            inputValue.trim().toLowerCase() !==
                              (
                                selectedCarrier.carrierName || ""
                              ).toLowerCase())) && (
                          <div
                            ref={divRef}
                            style={{
                              position: "absolute",
                              top: "100%",
                              left: "30%",
                              width: "70%",
                              backgroundColor: "white",
                              border: "1px solid #ccc",
                              borderRadius: "8px",
                              zIndex: 1000,
                            }}
                          >
                            {filteredNames.slice(0, 5).map((carrier, index) => (
                              <div
                                key={index}
                                style={{ padding: "5px", cursor: "pointer" }}
                                onClick={() => handleselectedCarrier(carrier)}
                              >
                                {carrier.carrierName}
                              </div>
                            ))}

                            {/* Add New Carrier Option */}
                            {inputValue &&
                              newNames.length === 0 &&
                              isAdmin &&
                              inputValue.trim().toLowerCase() !==
                                (
                                  selectedCarrier.carrierName || ""
                                ).toLowerCase() && (
                                <div
                                  style={{
                                    padding: "5px",
                                    cursor: "pointer",
                                    borderTop: "1px solid #ccc",
                                    textAlign: "center",
                                    fontWeight: "bold",
                                    backgroundColor: "#0072ce",
                                    color: "white",
                                    whiteSpace: "pre",
                                  }}
                                  onClick={() => handleAddCarrier(inputValue)}
                                >
                                  Create Carrier: {inputValue}
                                </div>
                              )}
                          </div>
                        )}
                      </Form.Group>

                      <Form.Group
                        controlId="formStatus"
                        className="d-flex align-items-center mb-1 position-relative"
                      >
                        <Form.Label className="mr-2" style={{ width: "30%" }}>
                          Status
                        </Form.Label>
                        <Form.Select
                          aria-label="Default select example"
                          style={getInputStyle({
                            width: "70%",
                          })}
                          onChange={handleChange}
                          name="status"
                          value={
                            selectedCarrier.status ? selectedCarrier.status : ""
                          }
                          disabled={!isNameSelected || !isAdmin}
                        >
                          <option value="" disabled>
                            Select status
                          </option>
                          <option value="inactive">Inactive</option>
                          <option value="active">Active</option>
                          <option value="other">Other</option>
                        </Form.Select>
                      </Form.Group>

                      <Form.Group
                        controlId="formOnboarded"
                        className="d-flex align-items-center mb-1"
                      >
                        <Form.Label className="mr-2" style={{ width: "30%" }}>
                          Onboarded
                        </Form.Label>
                        {isAdmin ? (
                          <OverlayTrigger
                            placement="auto"
                            overlay={
                              <Tooltip id="onboarded-tooltip">
                                The date will be displayed in the format set on
                                your computer
                              </Tooltip>
                            }
                            offset={[0, 0.5]}
                          >
                            <Form.Control
                              type="date"
                              className="pl-2"
                              style={getInputStyle({ width: "70%" })}
                              onChange={handleOnboardDateChange}
                              onBlur={handleOnboardDateBlur}
                              name="onboardDate"
                              value={onboardDateInput}
                              disabled={!isNameSelected || !isAdmin}
                            />
                          </OverlayTrigger>
                        ) : (
                          <Form.Control
                            type="date"
                            className="pl-2"
                            style={getInputStyle({ width: "70%" })}
                            onChange={handleOnboardDateChange}
                            onBlur={handleOnboardDateBlur}
                            name="onboardDate"
                            value={onboardDateInput}
                            disabled={!isNameSelected || !isAdmin}
                          />
                        )}
                      </Form.Group>

                      <Form.Group
                        controlId="formOffboarded"
                        className="d-flex align-items-center mb-1"
                      >
                        <Form.Label className="mr-2" style={{ width: "30%" }}>
                          Offboarded
                        </Form.Label>
                        {isAdmin ? (
                          <OverlayTrigger
                            placement="auto"
                            overlay={
                              <Tooltip id="offboarded-tooltip">
                                The date will be displayed in the format set on
                                your computer
                              </Tooltip>
                            }
                            offset={[0, 0.5]}
                          >
                            <Form.Control
                              type="date"
                              className="pl-2"
                              style={getInputStyle({ width: "70%" })}
                              onChange={handleOffboardDateChange}
                              onBlur={handleOffboardDateBlur}
                              name="offboardDate"
                              value={offboardDateInput}
                              disabled={!isNameSelected || !isAdmin}
                            />
                          </OverlayTrigger>
                        ) : (
                          <Form.Control
                            type="date"
                            className="pl-2"
                            style={getInputStyle({ width: "70%" })}
                            onChange={handleOffboardDateChange}
                            onBlur={handleOffboardDateBlur}
                            name="offboardDate"
                            value={offboardDateInput}
                            disabled={!isNameSelected || !isAdmin}
                          />
                        )}
                      </Form.Group>

                      <Form.Group
                        controlId="formRegion"
                        className="d-flex align-items-center mb-1"
                      >
                        <Form.Label className="mr-2" style={{ width: "30%" }}>
                          Regions
                        </Form.Label>
                        {isAdmin ? (
                          <OverlayTrigger
                            placement="auto"
                            overlay={
                              <Tooltip id="region tootltip">
                                {!isAdmin
                                  ? "You need admin privileges to edit"
                                  : "Select the region, you can select multiple regions"}
                              </Tooltip>
                            }
                            offset={[0, 0.5]}
                          >
                            <div
                              className="regions-container"
                              style={{
                                width: "70%",
                                display: "flex",
                                justifyContent: "space-between",
                              }}
                            >
                              {["GMNA", "GME", "GMSA", "GMIO"].map(
                                (regions) => (
                                  <Form.Check
                                    key={regions}
                                    type="checkbox"
                                    className="region-checkbox"
                                    label={regions}
                                    value={regions}
                                    checked={
                                      selectedCarrier.regions?.includes(
                                        regions,
                                      ) || false
                                    }
                                    onChange={handleRegionChange}
                                    disabled={!isNameSelected || !isAdmin}
                                    style={getInputStyle({
                                      marginRight: "10px",
                                    })}
                                  />
                                ),
                              )}
                            </div>
                          </OverlayTrigger>
                        ) : (
                          <div
                            className="regions-container"
                            style={{
                              width: "70%",
                              display: "flex",
                              justifyContent: "space-between",
                            }}
                          >
                            {["GMNA", "GME", "GMSA", "GMIO"].map((regions) => (
                              <Form.Check
                                key={regions}
                                type="checkbox"
                                className="region-checkbox"
                                label={regions}
                                value={regions}
                                checked={
                                  selectedCarrier.regions?.includes(regions) ||
                                  false
                                }
                                onChange={handleRegionChange}
                                disabled={!isNameSelected || !isAdmin}
                                style={getInputStyle({ marginRight: "10px" })}
                              />
                            ))}
                          </div>
                        )}
                      </Form.Group>
                    </Col>

                    <Col md={4}>
                      <Form.Group
                        controlId="formAddress"
                        className="d-flex align-items-start mb-1"
                      >
                        <Form.Label className="mr-2" style={{ width: "30%" }}>
                          Address
                        </Form.Label>
                        <div style={{ width: "70%" }}>
                          <Form.Control
                            type="text"
                            placeholder="Enter address line 1"
                            style={getInputStyle({ marginBottom: "5px" })}
                            onChange={handleChange}
                            name="address1"
                            value={
                              selectedCarrier.address1
                                ? selectedCarrier.address1
                                : ""
                            }
                            disabled={!isNameSelected || !isAdmin}
                          />
                          <Form.Control
                            type="text"
                            placeholder="Enter address line 2"
                            style={getInputStyle({ marginBottom: "5px" })}
                            onChange={handleChange}
                            name="address2"
                            value={
                              selectedCarrier.address2
                                ? selectedCarrier.address2
                                : ""
                            }
                            disabled={!isNameSelected || !isAdmin}
                          />
                        </div>
                      </Form.Group>

                      <Form.Group
                        controlId="formCity"
                        className="d-flex align-items-center mb-1"
                      >
                        <Form.Label className="mr-2" style={{ width: "30%" }}>
                          City
                        </Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Enter city"
                          style={getInputStyle({ width: "70%" })}
                          onChange={handleChange}
                          name="city"
                          value={
                            selectedCarrier.city ? selectedCarrier.city : ""
                          }
                          disabled={!isNameSelected || !isAdmin}
                        />
                      </Form.Group>

                      <Form.Group
                        controlId="formState"
                        className="d-flex align-items-center mb-1"
                      >
                        <Form.Label className="mr-2" style={{ width: "30%" }}>
                          State/Prov
                        </Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Enter state/province"
                          style={getInputStyle({ width: "70%" })}
                          onChange={handleChange}
                          name="state"
                          value={
                            selectedCarrier.state ? selectedCarrier.state : ""
                          }
                          disabled={!isNameSelected || !isAdmin}
                        />
                      </Form.Group>

                      <Form.Group
                        controlId="formCountry"
                        className="d-flex align-items-center mb-1"
                      >
                        <Form.Label className="mr-2" style={{ width: "30%" }}>
                          Country
                        </Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Enter country"
                          style={getInputStyle({ width: "70%" })}
                          onChange={handleChange}
                          name="country"
                          value={
                            selectedCarrier.country
                              ? selectedCarrier.country
                              : ""
                          }
                          disabled={!isNameSelected || !isAdmin}
                        />
                      </Form.Group>

                      <Form.Group
                        controlId="formPostcode"
                        className="d-flex align-items-center mb-1"
                      >
                        <Form.Label className="mr-2" style={{ width: "30%" }}>
                          Postcode/Zip
                        </Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Enter postcode/zip"
                          style={getInputStyle({ width: "70%" })}
                          onChange={handleChange}
                          name="postcode"
                          value={
                            selectedCarrier.postcode
                              ? selectedCarrier.postcode
                              : ""
                          }
                          disabled={!isNameSelected || !isAdmin}
                        />
                      </Form.Group>
                    </Col>

                    <Col
                      md={4}
                      className="ml-auto"
                      style={{
                        display: "flex",
                        justifyContent: "flex-end",
                      }}
                    >
                      <Form.Group
                        controlId="formNotes"
                        className="mb-1 position-relative"
                        style={{
                          width: "100%",
                          height: "75%",
                        }}
                      >
                        <Form.Control
                          as="textarea"
                          rows={10}
                          placeholder={
                            !isNameSelected
                              ? ""
                              : "No notes available for this carrier. You can add notes here."
                          }
                          style={getInputStyle({
                            height: "100%",
                            width: "100%",
                            resize: "none",
                            overflow: "auto",
                          })}
                          maxLength={1024}
                          name="notes"
                          onChange={handleChange}
                          value={
                            selectedCarrier.notes ? selectedCarrier.notes : ""
                          }
                          disabled={!isNameSelected || !isAdmin}
                        />
                        <span
                          className="char-count"
                          style={{
                            display: "block",
                            marginTop: "5px",
                            width: "100%",
                            textAlign: "right",
                            fontSize: "10px",
                            color: "green",
                          }}
                        >
                          {selectedCarrier?.notes
                            ? charCount - selectedCarrier?.notes?.length
                            : charCount}{" "}
                          Characters Remaining
                        </span>
                      </Form.Group>
                    </Col>
                  </Row>
                </Form>
                {/* End of Contact
                 */}
                <CancelModal
                  type={modalType}
                  show={showCancelModal}
                  setShow={setShowCancelModal}
                  data={cancelData}
                  cancelConfirm={cancelConfirm}
                />
                <DeleteModal
                  show={showDeleteModal}
                  setShow={setShowDeleteModal}
                  data={deleteData}
                  handleDeleteContact={handleDeleteContact}
                />
                {/* <h6>History</h6>
            <Row style={{ 
              gap: "1rem",
              marginLeft: "0",
              paddingLeft: "2px"
            }}>
              {history.map((item) => (
                <Card style={{ 
                  width: "15%",
                  marginTop: "10px",
                  marginBottom: "10px"
                }} key={item.id}>
                  <Card.Body>
                    <Table size="sm" borderless>
                      <tbody>
                        <tr>
                          <th>ID</th>
                          <td>{item.id}</td>
                        </tr>
                        <tr>
                          <th style={{ width: "10%", padding: "4px" }}>Name</th>
                          <td style={{ padding: "4px" }}>{item.name}</td>
                        </tr>
                        <tr>
                          <th style={{ width: "10%", padding: "4px" }}>Address</th>
                          <td style={{ padding: "4px" }}>{item.address}</td>
                        </tr>
                      </tbody>
                    </Table>
                  </Card.Body>
                </Card>
              ))}
            </Row> */}
              </Col>
            </Row>

            {isNameSelected && selectedCarrier.id !== undefined && (
              <ContactsTable
                contacts={sortedAndFilteredContacts}
                allFilterData={allFilterDataForContacts}
                handleSort={handleSort}
                sortConfig={sortConfig}
                selectedOptions={selectedOptions}
                setSelectedOptions={setSelectedOptions}
                handleInputChange={handleInputChange}
                handleSaveContact={handleSaveContact}
                handleCancelEdit={handleCancelEdit}
                handleEdit={handleEdit}
                editId={editId}
                isSortingLocked={isSortingLocked}
                handleAddContact={handleAddContact}
                isContactsModified={isContactsModified}
                timezoneData={timezoneData}
                languageData={languageData}
                cancelConfirm={cancelConfirm}
                handleDeleteContact={handleDeleteContact}
                showDeleteModal={showDeleteModal}
                setShowDeleteModal={setShowDeleteModal}
                setDeleteData={setDeleteData}
              />
            )}
            <CarrierUploadModal
              data={uploadMessage}
              show={showUploadModal}
              setShow={setShowUploadModal}
            />
            <CarrierUploadSelectModal
              show={showCarrierSelectModal}
              setShow={setShowCarrierSelectModal}
              carrierData={carrierData}
              selectedCarrierIds={selectedCarrierIds}
              setSelectedCarrierIds={setSelectedCarrierIds}
              handleCarrierTemplateDownload={handleCarrierTemplateDownload}
            />
            <ContactUploadSelectModal
              show={showContactSelectModal}
              setShow={setShowContactSelectModal}
              contactData={contactData}
              carrierData={carrierData}
              selectedContactIds={selectedContactIds}
              setSelectedContactIds={setSelectedContactIds}
              handleContactTemplateDownload={handleContactTemplateDownload}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default CarrierForm;
